import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Icone } from '../Icone';
import { IconePleine } from '../IconePleine';
import { gradientPour } from '../../lib/avatar';
import { useEnvoyerMessage } from '../../lib/useMessagerie';
import { useReponseRapide, type PopupMessage } from '../../messagerie/contexteReponseRapide';
import { ErreurApi } from '../../lib/api';

/** Délai avant fermeture automatique d'une pop-up sans interaction. */
const DUREE_AFFICHAGE_MS = 5000;

function CartePopupMessage({ popup, onFermer }: { popup: PopupMessage; onFermer: () => void }): JSX.Element {
  const [texte, setTexte] = useState('');
  /** Refus de l'envoi (mot bloqué, TF-484, ou autre) : le texte reste pour être reformulé. */
  const [refus, setRefus] = useState<string | null>(null);
  const envoyerMessage = useEnvoyerMessage(popup.conversationId);
  const minuteurFermetureRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const suspendreFermetureAutomatique = useCallback(() => {
    if (minuteurFermetureRef.current) clearTimeout(minuteurFermetureRef.current);
    minuteurFermetureRef.current = null;
  }, []);

  const programmerFermetureAutomatique = useCallback(() => {
    suspendreFermetureAutomatique();
    minuteurFermetureRef.current = setTimeout(onFermer, DUREE_AFFICHAGE_MS);
  }, [onFermer, suspendreFermetureAutomatique]);

  // Redémarre le délai à chaque nouveau message reçu pour ce fil ; le nettoyage
  // couvre à la fois le démontage et le changement de message.
  useEffect(() => {
    programmerFermetureAutomatique();
    return suspendreFermetureAutomatique;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [popup.conversationId, popup.envoyeLe]);

  function envoyer(): void {
    const contenu = texte.trim();
    if (!contenu || envoyerMessage.isPending) return;
    envoyerMessage.mutate(
      { texte: contenu },
      {
        onSuccess: onFermer,
        onError: (erreur) =>
          setRefus(erreur instanceof ErreurApi ? erreur.message : 'Message non envoyé, réessayez.'),
      },
    );
  }

  return (
    <div
      onMouseEnter={suspendreFermetureAutomatique}
      onMouseLeave={programmerFermetureAutomatique}
      onFocus={suspendreFermetureAutomatique}
      onBlur={(e) => {
        // Ne relance le délai que si le focus quitte vraiment la carte
        // (et non pour passer d'un champ à l'autre à l'intérieur).
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) programmerFermetureAutomatique();
      }}
      className="w-[calc(100vw-2rem)] animate-fade-in overflow-hidden rounded-xl border border-gray-100 bg-white shadow-xl sm:w-80"
    >
      <div className="flex items-center gap-2.5 border-b border-gray-100 p-3">
        <div
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br text-xs font-bold text-white ${gradientPour(popup.expediteur.id)}`}
        >
          {popup.expediteur.initiales}
        </div>
        <Link
          to={`/messages?conversation=${popup.conversationId}`}
          onClick={onFermer}
          className="min-w-0 flex-1 truncate text-sm font-semibold text-codialis-navy hover:underline"
        >
          {popup.expediteur.prenom} {popup.expediteur.nom}
        </Link>
        <button
          type="button"
          onClick={onFermer}
          aria-label="Fermer"
          className="shrink-0 rounded-lg p-1.5 text-codialis-gray transition hover:bg-gray-100"
        >
          <Icone nom="croix" className="h-4 w-4" />
        </button>
      </div>

      <p className="line-clamp-2 px-3 pt-2.5 text-sm text-codialis-gray">
        {popup.texte || (popup.aPieceJointe ? 'Pièce jointe' : '')}
      </p>

      <div className="flex items-center gap-2 p-3 pt-2.5">
        <input
          aria-label="Réponse rapide"
          value={texte}
          onChange={(e) => {
            setRefus(null);
            setTexte(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') envoyer();
          }}
          placeholder="Répondre..."
          className="w-full min-w-0 rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-codialis-mint"
        />
        <button
          type="button"
          onClick={envoyer}
          disabled={!texte.trim() || envoyerMessage.isPending}
          aria-label="Envoyer"
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-codialis-navy text-white transition hover:bg-codialis-deep disabled:opacity-40"
        >
          <IconePleine nom="envoyer" className="h-4 w-4" />
        </button>
      </div>
      {refus && (
        <p role="alert" className="px-3 pb-3 text-xs text-red-700">
          {refus}
        </p>
      )}
    </div>
  );
}

/**
 * Pop-ups façon Discord posées en bas à droite dès qu'un message arrive,
 * quel que soit l'écran ouvert — l'API ne les déclenche déjà pas si le
 * destinataire est en « ne pas déranger ».
 */
export function PopupReponseRapide(): JSX.Element | null {
  const { popups, fermer } = useReponseRapide();
  if (popups.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-40 flex flex-col-reverse gap-3">
      {popups.map((popup) => (
        <CartePopupMessage key={popup.conversationId} popup={popup} onFermer={() => fermer(popup.conversationId)} />
      ))}
    </div>
  );
}
